<template>
  <section id="how-to-report" class="relative overflow-hidden py-24 sm:py-28 md:py-32 bg-[#0a0f1a]">
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(59,130,246,0.15),_transparent_50%)]"></div>
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(99,102,241,0.1),_transparent_50%)]"></div>
      <div class="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
      <div class="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent"></div>
      
      <div class="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
      <div class="absolute bottom-20 right-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-float-delayed"></div>
    </div>

    <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Header -->
      <div class="text-center max-w-3xl mx-auto mb-2" >    
        <h2 class="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight animate-slide-up" style="margin-top: -50px;">
          {{ $t('howToReport.title') }}
          <span class="block mt-1 bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
            {{ $t('howToReport.titleHighlight') }}
          </span>
        </h2>
        
        <p class="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed animate-slide-up-delayed">
          {{ $t('howToReport.desc') }}
        </p>
      </div>

      <!-- Steps Grid -->
      <div class="grid md:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-20" >
        <div class="group relative animate-fade-in-up" style="animation-delay: 100ms">
          <div class="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-blue-600/0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          <div class="relative bg-slate-800/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 group-hover:border-blue-500/50 transition-all duration-500 group-hover:-translate-y-2 h-full">
            <div class="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div class="text-center mb-6">
              <div class="relative inline-flex items-center justify-center w-18 h-18 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg shadow-blue-500/25 group-hover:shadow-blue-500/40 transition-shadow duration-500">
                <span class="text-2xl font-bold text-white font-mono">01</span>
              </div>
            </div>
            
            <h3 class="text-xl font-bold text-white mb-1 text-center group-hover:text-blue-100 transition-colors duration-300">{{ $t('howToReport.step1Title') }}</h3>
            <p class="text-slate-400 text-center leading-relaxed text-sm">
              {{ $t('howToReport.step1Desc') }}
            </p>
            
            <div class="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
              <div class="w-8 h- rounded-full bg-blue-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="group relative animate-fade-in-up" style="animation-delay: 200ms">
          <div class="absolute inset-0 bg-gradient-to-br from-emerald-500/20 to-emerald-600/0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          <div class="relative bg-slate-800/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 group-hover:border-emerald-500/50 transition-all duration-500 group-hover:-translate-y-2 h-full">
            <div class="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div class="text-center mb-6">
              <div class="relative inline-flex items-center justify-center w-18 h-18 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-lg shadow-emerald-500/25 group-hover:shadow-emerald-500/40 transition-shadow duration-500">
                <span class="text-2xl font-bold text-white font-mono">02</span>
              </div>
            </div>
            
            <h3 class="text-xl font-bold text-white mb-3 text-center group-hover:text-emerald-100 transition-colors duration-300">{{ $t('howToReport.step2Title') }}</h3>
            <p class="text-slate-400 text-center leading-relaxed text-sm">
              {{ $t('howToReport.step2Desc') }}
            </p>
            
            <div class="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
              <div class="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3 -->
        <div class="group relative animate-fade-in-up" style="animation-delay: 300ms">
          <div class="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-violet-600/0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          <div class="relative bg-slate-800/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 group-hover:border-violet-500/50 transition-all duration-500 group-hover:-translate-y-2 h-full">
            <div class="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div class="text-center mb-6">
              <div class="relative inline-flex items-center justify-center w-18 h-18 rounded-2xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-lg shadow-violet-500/25 group-hover:shadow-violet-500/40 transition-shadow duration-500">
                <span class="text-2xl font-bold text-white font-mono">03</span>
              </div>
            </div>
            
            <h3 class="text-xl font-bold text-white mb-3 text-center group-hover:text-violet-100 transition-colors duration-300">{{ $t('howToReport.step3Title') }}</h3>
            <p class="text-slate-400 text-center leading-relaxed text-sm">
              {{ $t('howToReport.step3Desc') }}
            </p>
            
            <div class="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
              <div class="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 4 -->
        <div class="group relative animate-fade-in-up" style="animation-delay: 400ms">
          <div class="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-cyan-600/0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          <div class="relative bg-slate-800/40 backdrop-blur-xl rounded-3xl p-8 border border-slate-700/50 group-hover:border-cyan-500/50 transition-all duration-500 group-hover:-translate-y-2 h-full">
            <div class="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            
            <div class="text-center mb-6">
              <div class="relative inline-flex items-center justify-center w-18 h-18 rounded-2xl bg-gradient-to-br from-cyan-500 to-cyan-600 shadow-lg shadow-cyan-500/25 group-hover:shadow-cyan-500/40 transition-shadow duration-500">
                <span class="text-2xl font-bold text-white font-mono">04</span>
              </div>
            </div>
            
            <h3 class="text-xl font-bold text-white mb-3 text-center group-hover:text-cyan-100 transition-colors duration-300">{{ $t('howToReport.step4Title') }}</h3>
            <p class="text-slate-400 text-center leading-relaxed text-sm">
              {{ $t('howToReport.step4Desc') }}
            </p>
            
            <div class="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0">
              <div class="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA Section -->
      <div class="text-center animate-fade-in-up" style="animation-delay: 500ms">
        <div class="relative max-w-2xl mx-auto">
          <div class="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-cyan-500/10 rounded-3xl blur-2xl"></div>
          <div class="relative bg-slate-800/60 backdrop-blur-xl rounded-3xl p-10 border border-slate-700/50">
            
            <h3 class="text-2xl sm:text-3xl font-bold text-white mb-4">
              {{ $t('readyToReport') }}
            </h3>
            
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <router-link
                :to="getRoute('report-incident')"
                class="group inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl hover:from-blue-400 hover:to-blue-500 transition-all duration-300 transform hover:scale-[1.02] shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
              >
                {{ $t('reportIncidentNow') }}
                <svg class="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </router-link>
              
              <router-link
                :to="getRoute('followup')"
                class="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-slate-300 border-2 border-slate-600 rounded-xl hover:bg-slate-700/50 hover:border-slate-500 hover:text-white transition-all duration-300"
              >
                {{ $t('trackExistingReport') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HowToReportSection',
    computed: {
    currentLang() {
      return this.$route.params.lang || this.$i18n.locale || 'en';
    }
  },
  
  methods: {
    getRoute(routeName, routeParams = {}) {
      const params = { 
        lang: this.currentLang,
        ...routeParams 
      };
      return { name: routeName, params };
    },
    
    // ... other methods you might have ...
  }
};
</script>

<style scoped>
/* Custom animations */
@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
}

@keyframes float-delayed {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-30px) rotate(-5deg); }
}

@keyframes fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slide-up {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 10s ease-in-out infinite;
  animation-delay: 2s;
}

.animate-fade-in {
  animation: fade-in 0.6s ease-out forwards;
}

.animate-slide-up {
  animation: slide-up 0.8s ease-out forwards;
}

.animate-slide-up-delayed {
  animation: slide-up 0.8s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
}

.animate-fade-in-up {
  animation: fade-in-up 0.8s ease-out forwards;
  opacity: 0;
}

/* Custom sizing */
.w-18 {
  width: 4.5rem;
}

.h-18 {
  height: 4.5rem;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .animate-float,
  .animate-float-delayed,
  .animate-fade-in,
  .animate-slide-up,
  .animate-slide-up-delayed,
  .animate-fade-in-up,
  .animate-ping {
    animation: none !important;
    opacity: 1 !important;
  }
  
  .transition-all,
  .transition-transform,
  .transition-opacity,
  .transition-colors,
  .transition-shadow {
    transition: none !important;
  }
}
</style>
